import { useState, useMemo } from 'react'
import { shuffle } from 'lodash'
import { OtherTextField } from './OtherTextField'

interface SingleChoiceQuestionProps {
  options: { label: string; value: string }[]
  onSelect: (value: string) => void
  question: string
  description?: string
  randomizeOptions?: boolean
}

export function SingleChoiceQuestion({
  options,
  onSelect,
  question,
  description,
  randomizeOptions = false,
}: SingleChoiceQuestionProps) {
  const [selectedValue, setSelectedValue] = useState<string | null>(null)
  const [showOtherInput, setShowOtherInput] = useState(false)

  // Randomize options if enabled, but keep "Other" at the end
  const randomizedOptions = useMemo(() => {
    if (!randomizeOptions) {
      return options
    }

    const otherOption = options.find((opt) => opt.value === 'Other')
    const nonOtherOptions = options.filter((opt) => opt.value !== 'Other')

    // Shuffle non-Other options using lodash
    const shuffled = shuffle(nonOtherOptions)

    // Add "Other" at the end if it exists
    return otherOption ? [...shuffled, otherOption] : shuffled
  }, [options, randomizeOptions])

  const handleSelect = (value: string) => {
    setSelectedValue(value)

    if (value === 'Other') {
      setShowOtherInput(true)
    } else {
      setShowOtherInput(false)
      onSelect(value)
    }
  }

  const handleOtherSubmit = (otherValue: string) => {
    onSelect(otherValue)
  }

  return (
    <div className="flex h-full w-full flex-col justify-center px-12">
      <h1 className="mb-12 text-center text-3xl font-semibold text-white md:text-left md:text-5xl">{question}</h1>
      {description && <p className="mb-4 text-sm text-gray-400 md:mb-6">{description}</p>}

      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:gap-6">
        {randomizedOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => handleSelect(option.value)}
            className={`flex h-12 w-full items-center justify-center gap-2 rounded-md border px-6 py-2 text-sm font-medium transition-all md:w-auto md:px-8 ${
              selectedValue === option.value
                ? 'border-gray-400 bg-transparent text-white'
                : 'border-gray-600 bg-transparent text-white hover:border-gray-500'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {showOtherInput && <OtherTextField onSubmit={handleOtherSubmit} />}
    </div>
  )
}
